import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TagComponent } from 'shared/components/tag/tag.component';
import { ITransaction, TransactionTypeIds } from 'core/types/transaction.interface';
import { BalanceService } from 'core/services/balance.service';
import { ITransactionDetail } from 'core/types/transaction-detail.interface';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { catchError, finalize, of } from 'rxjs';
import { AuthService } from 'core';
import { formatTransactionDisplayDate } from 'shared/components/korviu-list/transaction-list/transaction-list.component';

type TransactionDetailField = {
  title: string;
  value: string;
  translateValue?: boolean;
};

@Component({
  selector: 'app-transaction-status-modal',
  imports: [NzModalModule, TranslatePipe, TagComponent, KorviuLoaderComponent],
  templateUrl: './transaction-status-modal.component.html',
  styleUrl: './transaction-status-modal.component.scss'
})
export class TransactionStatusModalComponent implements OnInit {
  readonly #translateService = inject(TranslateService);
  readonly #nzModalRef = inject(NzModalRef);
  readonly #nzModalData: { transaction: ITransaction } = inject(NZ_MODAL_DATA);
  readonly #balanceService = inject(BalanceService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #authService = inject(AuthService);

  readonly transactionDetail = signal<ITransactionDetail | null>(null);
  readonly isLoading = signal(true);
  readonly fields = computed<TransactionDetailField[]>(() => {
    const detail = this.transactionDetail();
    const dateTime = detail?.date_time || formatTransactionDisplayDate(this.transaction, this.currentLang);

    if (this.isTransfer) {
      return this._filterFields([
        { title: 'balance.type', value: 'balance.sharedGanas', translateValue: true },
        { title: 'balance.sender', value: this.senderName },
        { title: 'balance.receiver', value: this.receiverName },
        { title: 'balance.dateTime', value: dateTime }
      ]);
    }

    if (this.isPurchase) {
      const product = this.transaction?.purchase?.product || this.transaction?.product;

      return this._filterFields([
        { title: 'balance.type', value: 'balance.rewardRedemption', translateValue: true },
        { title: 'balance.product', value: detail?.product_name || product?.name || '' },
        { title: 'balance.brand', value: detail?.brand_name || '' },
        { title: 'balance.category', value: detail?.category_name || product?.categories?.[0]?.name || '' },
        { title: 'balance.dateTime', value: dateTime }
      ]);
    }

    if (this.isDeposit) {
      return this._filterFields([
        { title: 'balance.type', value: 'balance.ganasDeposit', translateValue: true },
        { title: 'balance.dateTime', value: dateTime }
      ]);
    }

    return this._filterFields([
      {
        title: 'balance.type',
        value: detail?.type || this.transaction?.transaction_type?.localized_name,
        translateValue: true
      },
      { title: 'balance.brand', value: detail?.brand_name || '' },
      { title: 'balance.category', value: detail?.category_name || '' },
      { title: 'balance.product', value: detail?.product_name || '' },
      { title: 'balance.dateTime', value: dateTime }
    ]);
  });

  get currentLang(): string {
    return this.#translateService.currentLang;
  }

  get isPurchase(): boolean {
    return this.transaction?.transaction_type?.id === TransactionTypeIds.PURCHASE;
  }

  get isTransfer(): boolean {
    return this.transaction?.transaction_type?.id === TransactionTypeIds.TRANSFER;
  }

  get isDeposit(): boolean {
    return (
      this.transaction?.transaction_type?.id === TransactionTypeIds.DEPOSIT ||
      this.transaction?.transaction_type?.name?.toLowerCase() === 'deposit'
    );
  }

  get isReimburse(): boolean {
    return this.transaction?.transaction_type?.id === TransactionTypeIds.REIMBURSE;
  }

  get isAmountPositive(): boolean {
    return +this.transaction?.amount > 0;
  }

  get isAmountNegative(): boolean {
    return +this.transaction?.amount < 0;
  }

  get transaction(): ITransaction {
    return this.#nzModalData.transaction;
  }

  get detail(): ITransactionDetail | null {
    return this.transactionDetail();
  }

  get amountDisplay(): string {
    return this.detail?.amount || this.transaction?.amount_display?.replace('-', '')?.replace('+', '') || '';
  }

  get counterpartName(): string {
    return this.counterpartFullName.split(' ')[0] || '';
  }

  get counterpartFullName(): string {
    return this.detail?.counterpart_name?.trim() || this.transaction?.counterpart?.full_name?.trim() || '';
  }

  get currentUserName(): string {
    return this.#authService.currentUser$()?.fullName?.trim() || '';
  }

  get senderName(): string {
    return this.isIncomingTransfer ? this.counterpartFullName : this.currentUserName;
  }

  get receiverName(): string {
    return this.isIncomingTransfer ? this.currentUserName : this.counterpartFullName;
  }

  get hasTransactionId(): boolean {
    return typeof (this.detail?.id ?? this.transaction.id) === 'number';
  }

  get isDepositSource(): boolean {
    return this.transaction.source === 'deposit';
  }

  get statusDisplay(): string {
    if (this.isDeposit) {
      return this.#translateService.instant('balance.deposited');
    }

    return (
      this.detail?.status ||
      this.transaction?.status_display ||
      this.#translateService.instant(this.transaction?.status?.localizable_name || '')
    );
  }

  get statusTagType(): 'error' | 'warning' | 'success' | 'info' {
    const status = this.detail?.status?.toLowerCase() || this.transaction?.status?.status_name?.toLowerCase() || '';

    if (status.includes('fail')) {
      return 'error';
    }

    if (status.includes('pending')) {
      return 'warning';
    }

    return 'info';
  }

  ngOnInit(): void {
    const transactionId = this.transaction.id;

    if (this.isDepositSource || typeof transactionId !== 'number') {
      this.isLoading.set(false);
      return;
    }

    this.#balanceService
      .getTransactionDetail(transactionId)
      .pipe(
        catchError(() => of(null)),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(detail => {
        this.transactionDetail.set(detail);
      });
  }

  close(): void {
    this.#nzModalRef.destroy();
  }

  private get isIncomingTransfer(): boolean {
    const direction = this.detail?.transfer_direction?.toLowerCase();

    if (direction === 'received') {
      return true;
    }

    if (direction === 'sent') {
      return false;
    }

    return this.isAmountPositive;
  }

  private _filterFields(fields: TransactionDetailField[]): TransactionDetailField[] {
    return fields.filter(field => !!field.value);
  }
}
